import type { GreetingCardSafeZone } from '@giftie/shared/greeting-cards'
import type { YuzuBlock, YuzuTemplatePage } from '@giftie/yuzu-integration'

export type YuzuRatioBounds = { x: number, y: number, width: number, height: number }
export type YuzuPageSize = { width: number, height: number, units: string }

const CSS_PIXELS_PER_INCH = 96

export type YuzuBlockBounds = {
  bounds: YuzuRatioBounds
  parentBounds: YuzuRatioBounds
}

type YuzuBlockBoundsOptions = {
  allowOutOfBounds?: boolean
}

const PAGE_BOUNDS: YuzuRatioBounds = { x: 0, y: 0, width: 1, height: 1 }

/** Returns the browser's physical CSS-pixel plane for one Yuzu page. */
export function getYuzuPageCssDimensions(pageSize: YuzuPageSize) {
  const widthInches = toInches(pageSize.width, pageSize.units)
  const heightInches = toInches(pageSize.height, pageSize.units)
  if (widthInches === null || heightInches === null) return null

  return {
    width: widthInches * CSS_PIXELS_PER_INCH,
    height: heightInches * CSS_PIXELS_PER_INCH,
  }
}

/** Converts protected Yuzu blocks into the normalized safe zones used by decoration controls. */
export function getYuzuProtectedSafeZones(
  page: YuzuTemplatePage,
  pageSize?: YuzuPageSize,
): GreetingCardSafeZone[] {
  const blockBounds = getYuzuBlockBounds(page, pageSize, { allowOutOfBounds: true })

  return [...blockBounds.entries()].flatMap(([blockId, entry]) => {
    const block = findBlock(page.blocks, blockId)
    if (!block || block.attributes.protected !== true) return []

    const visibleBounds = intersectBounds(entry.bounds, PAGE_BOUNDS)
    if (!visibleBounds) return []

    return [{
      id: `yuzu-protected-${block.id}`,
      label: block.name || 'Protected area',
      textFlow: 'blocks-text' as const,
      bounds: visibleBounds,
    }]
  })
}

/** Resolves every parseable Yuzu block into page-relative bounds. */
export function getYuzuBlockBounds(
  page: YuzuTemplatePage,
  pageSize?: YuzuPageSize,
  options: YuzuBlockBoundsOptions = {},
): Map<string, YuzuBlockBounds> {
  const result = new Map<string, YuzuBlockBounds>()

  function collect(blocks: YuzuBlock[], parentBounds: YuzuRatioBounds) {
    blocks.forEach((block) => {
      const bounds = getAbsoluteBounds(block, parentBounds, pageSize, options)
      if (!bounds) return

      result.set(block.id, { bounds, parentBounds })
      collect(
        block.children.filter((child): child is YuzuBlock => typeof child === 'object'),
        bounds,
      )
    })
  }

  collect(page.blocks, { x: 0, y: 0, width: 1, height: 1 })
  return result
}

function findBlock(blocks: YuzuBlock[], blockId: string): YuzuBlock | null {
  for (const block of blocks) {
    // Decoration children move with their parent; they are not fixed obstacles.
    if (block.attributes.giftieDecoration === true) continue
    if (block.id === blockId) return block
    const nested = findBlock(
      block.children.filter((child): child is YuzuBlock => typeof child === 'object'),
      blockId,
    )
    if (nested) return nested
  }

  return null
}

function getAbsoluteBounds(
  block: YuzuBlock,
  parent: YuzuRatioBounds,
  pageSize?: YuzuPageSize,
  options: YuzuBlockBoundsOptions = {},
): YuzuRatioBounds | null {
  const left = readLength(block.styles.left, 0, pageSize?.width, pageSize?.units)
  const top = readLength(block.styles.top, 0, pageSize?.height, pageSize?.units)
  const width = readLength(block.styles.width, 1, pageSize?.width, pageSize?.units)
  const height = readLength(block.styles.height, 1, pageSize?.height, pageSize?.units)
  if (left === null || top === null || width === null || height === null) return null

  const bounds = {
    x: parent.x + (left.relativeToParent ? left.ratio * parent.width : left.ratio),
    y: parent.y + (top.relativeToParent ? top.ratio * parent.height : top.ratio),
    width: width.relativeToParent ? width.ratio * parent.width : width.ratio,
    height: height.relativeToParent ? height.ratio * parent.height : height.ratio,
  }
  if (
    bounds.width <= 0
    || bounds.height <= 0
    || (!options.allowOutOfBounds && (
      bounds.x < 0
      || bounds.y < 0
      || bounds.x + bounds.width > 1
      || bounds.y + bounds.height > 1
    ))
  ) return null

  return bounds
}

function intersectBounds(
  bounds: YuzuRatioBounds,
  boundary: YuzuRatioBounds,
): YuzuRatioBounds | null {
  if (
    bounds.x >= boundary.x
    && bounds.y >= boundary.y
    && bounds.x + bounds.width <= boundary.x + boundary.width
    && bounds.y + bounds.height <= boundary.y + boundary.height
  ) return bounds

  const x = Math.max(bounds.x, boundary.x)
  const y = Math.max(bounds.y, boundary.y)
  const right = Math.min(bounds.x + bounds.width, boundary.x + boundary.width)
  const bottom = Math.min(bounds.y + bounds.height, boundary.y + boundary.height)
  if (right <= x || bottom <= y) return null

  return {
    x,
    y,
    width: right - x,
    height: bottom - y,
  }
}

type ResolvedLength = { ratio: number, relativeToParent: boolean }

/** Resolves relative lengths and Yuzu physical units into page-normalized coordinates. */
function readLength(
  value: unknown,
  fallback: number,
  pageLength?: number,
  pageUnits?: string,
): ResolvedLength | null {
  if (value === undefined) return { ratio: fallback, relativeToParent: true }
  if (typeof value === 'number') {
    return Number.isFinite(value) && value >= 0 && value <= 1
      ? { ratio: value, relativeToParent: true }
      : null
  }
  if (typeof value !== 'string') return null

  const normalized = value.trim()
  const parsed = Number.parseFloat(normalized)
  if (!Number.isFinite(parsed)) return null
  if (normalized.endsWith('%')) return { ratio: parsed / 100, relativeToParent: true }
  if (parsed === 0) return { ratio: 0, relativeToParent: false }

  const unit = normalized.match(/[a-z]+$/i)?.[0]?.toLowerCase() ?? ''
  const absoluteInches = toInches(parsed, unit)
  const pageInches = pageLength && pageUnits ? toInches(pageLength, pageUnits) : null
  if (absoluteInches !== null && pageInches && pageInches > 0) {
    return { ratio: absoluteInches / pageInches, relativeToParent: false }
  }

  return parsed >= 0 && parsed <= 1
    ? { ratio: parsed, relativeToParent: true }
    : null
}

function toInches(value: number, rawUnit: string): number | null {
  const unit = rawUnit.trim().toLowerCase()
  if (unit === 'in' || unit === 'inch' || unit === 'inches') return value
  if (unit === 'mm') return value / 25.4
  if (unit === 'cm') return value / 2.54
  if (unit === 'pt') return value / 72
  if (unit === 'pc') return value / 6
  if (unit === 'px') return value / CSS_PIXELS_PER_INCH
  if (unit === 'q') return value / 101.6
  return null
}
